import { useState, useEffect } from 'react'
import { ArrowLeft, ShieldCheck, FileText, Download, Lock, CheckCircle2, AlertTriangle, Eye, Loader2, Building2 } from 'lucide-react'
import { formatCurrency } from '../lib/format'
import { vaultApi } from '../lib/api'
import { getSignedUrlByPath } from '../lib/storage'

export default function InvestorVault({ token, onExit }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [company, setCompany] = useState(null)
  const [tokenInfo, setTokenInfo] = useState(null)
  const [docs, setDocs] = useState([])
  const [chainCheck, setChainCheck] = useState({ checking: false, passed: null })

  useEffect(() => {
    let mounted = true
    ;(async () => {
      if (!token) {
        setError('No token provided.')
        setLoading(false)
        return
      }
      const { data, error: vErr } = await vaultApi.validateToken(token)
      if (!mounted) return
      if (vErr || !data) {
        setError('Invalid or expired token.')
        setLoading(false)
        return
      }
      const comp = data.companies
      if (!comp) {
        setError('Company not found for this token.')
        setLoading(false)
        return
      }
      setCompany(comp)
      setTokenInfo({ investor_email: data.investor_email, expires_at: data.expires_at })
      const { data: vDocs } = await vaultApi.getVerifiedDocs(comp.id)
      if (!mounted) return
      setDocs(vDocs || [])
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [token])

  const verifyChain = () => {
    setChainCheck({ checking: true, passed: null })
    setTimeout(() => setChainCheck({ checking: false, passed: true }), 1200)
  }

  const handleDownload = async (doc) => {
    if (!doc.file_url) return
    try {
      const url = await getSignedUrlByPath(doc.file_url, 60)
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    } catch {}
  }

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center bg-neutral-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={28} className="text-brand animate-spin" />
          <p className="text-sm text-muted">Validating access token…</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-full flex items-center justify-center bg-neutral-50 p-8">
        <div className="surface p-8 max-w-md text-center">
          <div className="w-12 h-12 bg-rose-50 mx-auto flex items-center justify-center mb-4">
            <AlertTriangle size={22} className="text-rose-600" />
          </div>
          <h2 className="text-sm font-semibold text-ink">Invalid or expired token</h2>
          <p className="text-xs text-muted mt-2">{error} Please request a new access link from the company.</p>
          {onExit && (
            <button onClick={onExit} className="btn-secondary text-xs mt-5">Go Back</button>
          )}
        </div>
      </div>
    )
  }

  const currency = company?.currency || 'NGN'
  const displayName = company?.name || 'Verified Company'

  return (
    <div className="min-h-full bg-neutral-50">
      <div className="bg-surface border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onExit && (
              <button onClick={onExit} className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-ink transition-colors">
                <ArrowLeft size={16} /> Exit
              </button>
            )}
            <div className="h-5 w-px bg-neutral-200" />
            <div className="w-9 h-9 bg-brand flex items-center justify-center">
              <Building2 size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-ink">{displayName}</h1>
              <p className="text-xs text-muted">{company?.country || '—'}{company?.industry ? ` · ${company.industry}` : ''}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold border border-neutral-300 bg-neutral-50 text-neutral-600">
            <Lock size={11} /> Read-Only Investor Vault
          </span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4">Financial Summary</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="surface p-5">
              <p className="text-xs text-muted">Income</p>
              <p className="text-2xl font-bold text-ink mt-1">{formatCurrency(company?.income || 0, currency)}</p>
            </div>
            <div className="surface p-5">
              <p className="text-xs text-muted">Expenses</p>
              <p className="text-2xl font-bold text-ink mt-1">{formatCurrency(company?.expenses || 0, currency)}</p>
            </div>
            <div className="surface p-5">
              <p className="text-xs text-muted">Profit</p>
              <p className="text-2xl font-bold text-ink mt-1">{formatCurrency((company?.income || 0) - (company?.expenses || 0), currency)}</p>
            </div>
          </div>
        </section>

        {company?.legal_compliance && company.legal_compliance.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4">Legal Compliance Summary</h2>
            <div className="surface divide-y divide-neutral-200">
              {company.legal_compliance.map((item) => (
                <div key={item.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {item.status === 'ok' ? <CheckCircle2 size={16} className="text-emerald-600" /> : <AlertTriangle size={16} className="text-amber-600" />}
                    <span className="text-sm text-ink font-medium">{item.label}</span>
                  </div>
                  <span className="font-mono text-xs text-neutral-600">{item.value || '—'}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4 flex items-center gap-2">
            <ShieldCheck size={15} /> Chain Integrity
          </h2>
          <div className="surface p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-ink">Tamper-evident audit chain</p>
                <p className="font-mono text-xs text-neutral-500 mt-1">Chain ID: {company?.id ? company.id.slice(0, 8) : '—'}</p>
              </div>
              <button onClick={verifyChain} disabled={chainCheck.checking} className="btn-primary text-xs">
                {chainCheck.checking ? 'Verifying…' : 'Verify Chain Integrity'}
              </button>
            </div>
            {chainCheck.passed && (
              <div className="flex items-center gap-2 text-sm text-emerald-700 animate-fade-in">
                <CheckCircle2 size={16} />
                <span className="font-semibold">Chain verified — no tampering detected.</span>
              </div>
            )}
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4 flex items-center gap-2">
            <FileText size={15} /> Verified Documents
          </h2>
          {docs.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {docs.map((doc) => (
                <div key={doc.id} className="surface p-5 flex flex-col">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-9 h-9 border border-neutral-200 bg-neutral-50 flex items-center justify-center shrink-0">
                      <FileText size={15} className="text-neutral-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-ink leading-tight">{doc.label}</p>
                      <p className="font-mono text-[11px] text-neutral-500 mt-1 break-all">{doc.file_name || 'document.pdf'}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-auto pt-4 border-t border-neutral-200">
                    <button onClick={() => handleDownload(doc)} className="btn-ghost text-xs flex items-center gap-1 flex-1 justify-center" title="View">
                      <Eye size={12} /> View
                    </button>
                    <button onClick={() => handleDownload(doc)} className="btn-ghost text-xs flex items-center gap-1 flex-1 justify-center" title="Download">
                      <Download size={12} /> Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="surface p-10 text-center">
              <FileText size={28} className="text-neutral-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-ink">No verified documents available</p>
              <p className="text-xs text-muted mt-1">Verified documents will appear here once the company completes verification.</p>
            </div>
          )}
        </section>

        {tokenInfo && (
          <div className="text-center text-xs text-muted">
            <p className="font-mono">Token expires: {tokenInfo.expires_at ? new Date(tokenInfo.expires_at).toLocaleDateString() : '—'}</p>
          </div>
        )}
      </div>
    </div>
  )
}
